import React, { useEffect, useState } from "react";
import Shell from "@/components/Shell";
import { api } from "@/lib/api";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Cell } from "recharts";
import { DollarSign, Trophy, Users, Mail, GitBranch, CheckCircle2 } from "lucide-react";
import WeeklyDigest from "@/components/outreach/WeeklyDigest";
import LeadMagnetFunnel from "@/components/outreach/LeadMagnetFunnel";

const PALETTE = ["hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-3))", "hsl(var(--chart-4))", "hsl(var(--chart-5))", "hsl(var(--chart-1))"];

export default function Analytics() {
  const [data, setData] = useState(null);
  useEffect(() => { (async () => { const r = await api.get("/analytics/overview"); setData(r.data); })(); }, []);
  if (!data) return <Shell title="Analytics"><div className="p-6 text-sm text-muted-foreground">Loading...</div></Shell>;
  const kpis = [
    { label: "Pipeline value", value: `$${(data.pipeline_value || 0).toLocaleString()}`, test: "kpi-pipeline", icon: DollarSign, accent: true },
    { label: "Won", value: `$${(data.won_value || 0).toLocaleString()}`, test: "kpi-won", icon: Trophy },
    { label: "Contacts", value: data.contacts, test: "kpi-contacts", icon: Users },
    { label: "Emails sent", value: data.emails_sent, test: "kpi-emails", icon: Mail },
    { label: "Open deals", value: data.deals_open, test: "kpi-open", icon: GitBranch },
    { label: "Won deals", value: data.deals_won, test: "kpi-won-count", icon: CheckCircle2 },
  ];
  return (
    <Shell title="Analytics">
      <div className="p-6 space-y-6">
        <LeadMagnetFunnel />
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 stagger">
          {kpis.map(({ icon: Icon, ...k }) => (
            <div key={k.label} className={`relative overflow-hidden border rounded-lg p-4 lift ${k.accent ? "bg-primary text-primary-foreground border-primary shadow-[0_8px_24px_hsl(var(--primary)/0.3)]" : "bg-card border-border"}`} data-testid={k.test}>
              <Icon className={`absolute right-3 top-3 w-4 h-4 ${k.accent ? "text-primary-foreground/60" : "text-muted-foreground/60"}`} />
              <div className={`eyebrow ${k.accent ? "text-primary-foreground/70" : ""}`}>{k.label}</div>
              <div className="display text-2xl font-extrabold tracking-tight mt-2 mono">{k.value}</div>
            </div>
          ))}
        </div>
        <WeeklyDigest />
        <div className="border border-border rounded-lg bg-card p-5">
          <div className="flex items-baseline justify-between mb-4"><div><div className="eyebrow">Pipeline</div><div className="text-sm font-semibold mt-0.5">Value by stage</div></div><div className="text-xs mono text-muted-foreground">{data.by_stage?.length || 0} stages</div></div>
          <div className="h-64 min-h-[16rem] w-full">
            <ResponsiveContainer width="100%" height="100%" minHeight={256}>
              <BarChart data={data.by_stage} barCategoryGap="28%">
                <CartesianGrid stroke="hsl(var(--border) / 0.6)" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="stage" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v >= 1000 ? `${Math.round(v / 1000)}k` : v}`} />
                <Tooltip cursor={{ fill: "hsl(var(--accent) / 0.5)" }} contentStyle={{ background: "hsl(var(--popover) / 0.85)", backdropFilter: "blur(12px)", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12, boxShadow: "var(--shadow-lift)" }} formatter={(v) => [`$${Number(v).toLocaleString()}`, "Value"]} />
                <Bar dataKey="value" radius={[6, 6, 2, 2]}>{(data.by_stage || []).map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </Shell>
  );
}
